import { useState, useEffect } from 'react';
import useApi from '../api';
import { JourneyEvent } from './types';
import {
  SessionCreatedEvent,
  SessionActiveEvent,
  SessionInactiveEvent,
  LeadCreatedEvent,
  LeadUpdatedEvent,
  ConversationEscalatedEvent,
  RepTookOverChatEvent,
  BuyerIntentEvent,
  VisitorWarmEvent,
  CalendarShownEvent,
  MeetingBookedEvent,
  AssessmentStartedEvent,
  AssessmentCompletedEvent,
  ReportGeneratedEvent,
  RoutingDecisionEvent,
  TrackClassificationEvent,
  UserIdentifiedEvent,
  UserUpdatedEvent,
  SessionFlaggedEvent,
} from './events';
import { Clock } from 'lucide-react';
import SectionHeader from '@/components/ui/SectionHeader';

interface VisitorJourneyProps {
  conversationId?: string;
  visitorId?: string;
  leadId?: string;
  /** When true, the component renders without its own header/card padding so it
   *  can be embedded inside a CollapsibleSection that supplies the title. */
  embedded?: boolean;
  /** Use the larger icon-and-title treatment shared by contact detail sections. */
  contactDetail?: boolean;
}

const SUPPORTED_EVENT_TYPES = new Set([
  'new_session_created',
  'session_active',
  'session_inactive',
  'lead_created',
  'lead_updated',
  'conversation_escalated',
  'rep_took_over_chat',
  'buyer_intent_triggered',
  'visitor_warm',
  'calendar_shown',
  'lead_meeting_booked',
  'assessment_started',
  'assessment_completed',
  'report_generated',
  'routing_decision',
  'track_classification',
  'user_identified',
  'user_updated',
  'session_flagged',
]);

export default function VisitorJourney({ conversationId, visitorId, leadId, embedded = false, contactDetail = false }: VisitorJourneyProps) {
  const [events, setEvents] = useState<JourneyEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { get } = useApi();

  useEffect(() => {
    const fetchEvents = async () => {
      // At least one ID must be provided
      if (!conversationId && !visitorId && !leadId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Build query params
        const params = new URLSearchParams();
        if (conversationId) params.append('conversation_id', conversationId);
        if (visitorId) params.append('visitor_id', visitorId);
        if (leadId) params.append('lead_id', leadId);
        
        const response = await get(`/events?${params.toString()}`);
        // Reverse to show oldest first (chronological order)
        const eventsData = response.data.events || [];
        setEvents([...eventsData].reverse());
      } catch (err) {
        console.error('Error fetching events:', err);
        setError('Failed to load journey');
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [conversationId, visitorId, leadId]);

  const renderEvent = (event: JourneyEvent) => {
    switch (event.event_type) {
      case 'new_session_created':
        return <SessionCreatedEvent key={event.id} event={event} />;
      case 'session_active':
        return <SessionActiveEvent key={event.id} event={event} />;
      case 'session_inactive':
        return <SessionInactiveEvent key={event.id} event={event} />;
      case 'lead_created':
        return <LeadCreatedEvent key={event.id} event={event} />;
      case 'lead_updated':
        return <LeadUpdatedEvent key={event.id} event={event} />;
      case 'conversation_escalated':
        return <ConversationEscalatedEvent key={event.id} event={event} />;
      case 'rep_took_over_chat':
        return <RepTookOverChatEvent key={event.id} event={event} />;
      case 'buyer_intent_triggered':
        return <BuyerIntentEvent key={event.id} event={event} />;
      case 'visitor_warm':
        return <VisitorWarmEvent key={event.id} event={event} />;
      case 'calendar_shown':
        return <CalendarShownEvent key={event.id} event={event} />;
      case 'lead_meeting_booked':
        return <MeetingBookedEvent key={event.id} event={event} />;
      case 'assessment_started':
        return <AssessmentStartedEvent key={event.id} event={event} />;
      case 'assessment_completed':
        return <AssessmentCompletedEvent key={event.id} event={event} />;
      case 'report_generated':
        return <ReportGeneratedEvent key={event.id} event={event} />;
      case 'routing_decision':
        return <RoutingDecisionEvent key={event.id} event={event} />;
      case 'track_classification':
        return <TrackClassificationEvent key={event.id} event={event} />;
      case 'user_identified':
        return <UserIdentifiedEvent key={event.id} event={event} />;
      case 'user_updated':
        return <UserUpdatedEvent key={event.id} event={event} />;
      case 'session_flagged':
        return <SessionFlaggedEvent key={event.id} event={event} />;
      default:
        return null;
    }
  };

  // Unsupported event types do not render a row, so exclude them before
  // calculating groups, spacing, connector lines, and the displayed count.
  const visibleEvents = events.filter((event) => SUPPORTED_EVENT_TYPES.has(event.event_type));

  // Group visible events by conversation.
  const groupedEvents = visibleEvents.reduce((acc, event) => {
    const conversationId = event.conversation_id || 'no_conversation';
    if (!acc[conversationId]) {
      acc[conversationId] = [];
    }
    acc[conversationId].push(event);
    return acc;
  }, {} as Record<string, JourneyEvent[]>);
  const eventGroups = Object.entries(groupedEvents);
  const showConversationLabels = eventGroups.length > 1;

  if (loading) {
    const loadingContent = (
      <div className={`${embedded || contactDetail ? "" : "p-4 "}space-y-4 animate-pulse`}>
        {!embedded && !contactDetail && (
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 bg-muted rounded" />
            <div className="h-4 w-28 bg-muted rounded" />
          </div>
        )}
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex items-start gap-3 pl-2">
            <div className="w-6 h-6 rounded-full bg-muted flex-shrink-0 mt-0.5" />
            <div className="flex-1 space-y-1.5">
              <div className="h-4 w-40 bg-muted rounded" />
              <div className="h-3 w-24 bg-muted rounded" />
            </div>
          </div>
        ))}
      </div>
    );
    return contactDetail ? (
      <div className="space-y-2">
        <SectionHeader title="Visitor journey" icon={Clock} />
        <div className="rounded-xl border bg-card p-4">{loadingContent}</div>
      </div>
    ) : loadingContent;
  }

  if (error) {
    const errorContent = (
      <div className="flex items-center justify-center p-12 text-destructive">
        <p>{error}</p>
      </div>
    );
    return contactDetail ? (
      <div className="space-y-2">
        <SectionHeader title="Visitor journey" icon={Clock} />
        <div className="rounded-xl border bg-card">{errorContent}</div>
      </div>
    ) : errorContent;
  }

  if (visibleEvents.length === 0) {
    const emptyContent = (
      <div className="flex flex-col items-center justify-center p-12 text-muted-foreground">
        <Clock className="w-16 h-16 mb-4 opacity-50" />
        <p className="text-lg font-medium">No journey events yet</p>
        <p className="text-sm mt-2">Events will appear here as the visitor interacts</p>
      </div>
    );
    return contactDetail ? (
      <div className="space-y-2">
        <SectionHeader title="Visitor journey" icon={Clock} />
        <div className="rounded-xl border bg-card">{emptyContent}</div>
      </div>
    ) : emptyContent;
  }

  const journeyContent = (
    <div className={`${embedded || contactDetail ? "" : "p-4 "}space-y-4`}>
      {!embedded && !contactDetail && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              Visitor Journey
            </h3>
          </div>
          <span className="text-xs text-muted-foreground">
            {visibleEvents.length} event{visibleEvents.length !== 1 ? 's' : ''}
          </span>
        </div>
      )}

      <div className="max-h-[500px] space-y-5 overflow-x-hidden overflow-y-auto pr-1 scrollbar-thin">
        {eventGroups.map(([conversationId, conversationEvents], groupIndex) => (
          <section key={conversationId} aria-label={conversationId === 'no_conversation' ? 'Visitor activity' : `Conversation ${groupIndex + 1}`}>
            {showConversationLabels && (
              <div className="mb-3 flex items-center gap-2">
                <div className="h-px flex-1 bg-border" />
                <span className="px-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                  {conversationId === 'no_conversation' ? 'Visitor activity' : `Conversation ${groupIndex + 1}`}
                </span>
                <div className="h-px flex-1 bg-border" />
              </div>
            )}
            <div className="space-y-0">
              {conversationEvents.map((event, index) => {
                const isLast = index === conversationEvents.length - 1;
                return (
                  <div key={event.id} className="relative pb-5 last:pb-0">
                    {/* Connector line linking this node to the next one */}
                    {!isLast && (
                      <span
                        aria-hidden
                        className="absolute bottom-0 left-[13.5px] top-7 w-px bg-border"
                      />
                    )}
                    {renderEvent(event)}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
  return contactDetail ? (
    <div className="space-y-2">
      <SectionHeader
        title="Visitor journey"
        icon={Clock}
        trailing={(
          <span className="pt-1 text-xs text-muted-foreground">
            {visibleEvents.length} event{visibleEvents.length !== 1 ? 's' : ''}
          </span>
        )}
      />
      <div className="rounded-xl border bg-card p-4">{journeyContent}</div>
    </div>
  ) : journeyContent;
}
